import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { generateConfig } from "../../utils/api";
import { handleApiError } from "../../utils/apiErrorHandler";

// Fetch all users with calendars (clinicians, admins, owners, etc.)
// This is used for scheduling and meetings to include ALL users who have calendars
export const fetchUsersWithCalendars = createAsyncThunk(
  "clinicians/fetchUsersWithCalendars",
  async (filters = {}, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      // Build query parameters
      const params = new URLSearchParams();
      if (filters.clinicId) {
        params.append('clinicId', filters.clinicId);
      }
      if (filters.status) {
        params.append('status', filters.status);
      }

      const queryString = params.toString();
      const url = `${baseURL}/api/users/with-calendars${queryString ? `?${queryString}` : ''}`;

      console.log(`[Fetch Users With Calendars] Fetching from: ${url}`);
      const response = await axios.get(url, config);
      console.log(`[Fetch Users With Calendars] Received ${response.data.length} users`);
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const createClinician = createAsyncThunk(
  "clinicians/createClinician",
  async (clinicianData, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.post(
        `${baseURL}/api/clinicians`,
        clinicianData,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchClinicians = createAsyncThunk(
  "clinicians/fetchClinicians",
  async (filters = {}, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      // Build query parameters
      const params = new URLSearchParams();
      if (filters.search) {
        params.append('search', filters.search);
      }
      if (filters.clinicId) {
        params.append('clinicId', filters.clinicId);
      }
      if (filters.status) {
        params.append('status', filters.status);
      }

      const queryString = params.toString();
      const url = `${baseURL}/api/clinicians${queryString ? `?${queryString}` : ''}`;

      const response = await axios.get(url, config);
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const searchClinicians = createAsyncThunk(
  "clinicians/searchClinicians",
  async (searchQuery, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.get(
        `${baseURL}/api/clinicians/search?q=${encodeURIComponent(searchQuery)}`,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchClinicianById = createAsyncThunk(
  "clinicians/fetchClinicianById",
  async (id, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.get(
        `${baseURL}/api/clinicians/${id}`,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchTreatingClinician = createAsyncThunk(
  "clinicians/fetchTreatingClinician",
  async (id, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.get(
        `${baseURL}/api/clinicians/${id}`,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchSupervisingClinician = createAsyncThunk(
  "clinicians/fetchSupervisingClinician",
  async (id, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.get(
        `${baseURL}/api/clinicians/${id}`,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchCliniciansByClinicId = createAsyncThunk(
  "clinicians/fetchCliniciansByClinicId",
  async (clinicId, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.get(
        `${baseURL}/api/clinicians/clinic/${clinicId}`,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const updateClinician = createAsyncThunk(
  "clinicians/updateClinician",
  async ({ id, clinicianData }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.put(
        `${baseURL}/api/clinicians/${id}`,
        clinicianData,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const deleteClinician = createAsyncThunk(
  "clinicians/deleteClinician",
  async (id, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.delete(
        `${baseURL}/api/clinicians/${id}`,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);
